import { type CSSProperties, type ReactElement, type ReactNode } from 'react';

import { cn } from '../../lib/cn';

export interface ScrollableRegionProps {
  /**
   * Accessible name for the region, already localized. Required: an unnamed
   * region drops a keyboard user into an anonymous box, which is worse than not
   * announcing one at all. Usually the same string as the table's `<caption>`.
   */
  'aria-label': string;
  /** Classes for the region itself — the `overflow-*` rule, borders, margins. */
  className?: string;
  /** Inline styles the region needs at the call site (e.g. a capped height). */
  style?: CSSProperties;
  children: ReactNode;
}

/**
 * A box that scrolls, and that a keyboard can actually reach.
 *
 * `<div className="overflow-x-auto">` around a wide table is the standard
 * responsive-table idiom, and on its own it fails WCAG 2.1.1: a mouse can pan
 * it, a keyboard cannot. Unless the box happens to contain something focusable,
 * its content is unreachable without a pointer. axe flags it as
 * `scrollable-region-focusable` (serious) — first caught by the Storybook
 * runner's wide-table story here, then again by an axe pass over the built
 * marketing site, which found the same shape on `/pricing` and
 * `/pricing/build-vs-buy` plus nine more wrappers waiting for the viewport that
 * would trigger them. Two apps hitting the same defect is what moved the recipe
 * out of `Table` and into a primitive.
 *
 * `tabIndex={0}` makes the box a tab stop the arrow keys scroll; `role="region"`
 * plus the required label means assistive tech announces what was entered.
 *
 * Deliberately unconditional rather than measured at runtime: the marketing site
 * is prerendered and must work before (and without) hydration, so a tab stop
 * that only appeared once JS had measured the overflow would leave a no-JS
 * keyboard user with no way in. A table whose content fits gains one extra tab
 * stop; that is the cheaper half of the trade.
 *
 * Not for boxes that already contain focusable content — a nav strip of links is
 * reachable through those links and needs nothing here.
 */
export function ScrollableRegion({
  'aria-label': ariaLabel,
  className,
  style,
  children,
}: ScrollableRegionProps): ReactElement {
  return (
    <div
      role="region"
      aria-label={ariaLabel}
      tabIndex={0}
      // A new tab stop needs a visible focus indicator, or the keyboard user it
      // was added for cannot tell they have arrived.
      className={cn(
        /*
         * `relative` is LOAD-BEARING, not decoration, and it was missing.
         *
         * A scroller whose content is WIDER than it — the whole case this
         * component exists for — leaks that width into an ancestor's
         * `scrollWidth` when the scroller is `position: static`, so the PAGE
         * scrolls sideways while the region itself scrolls correctly. That is
         * the `ui-design.md` §7 failure the region is supposed to prevent,
         * arriving through the component meant to prevent it.
         *
         * Measured in Chromium at 390px with a 544px table inside a 308px
         * region: `main.scrollWidth` 534 against a 390 client. `width: 100%`,
         * `max-width: 100%` + `min-width: 0`, `display: grid` and
         * `isolation: isolate` all left it at 534; `position: relative` and
         * `contain: paint` both returned it to 390. `relative` is the one kept
         * — it is the ordinary idiom, and it also makes the region a containing
         * block for anything a caller positions inside it, where `contain`
         * would silently change stacking and paint behaviour for every caller.
         */
        'relative',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
        className,
      )}
      style={style}
    >
      {children}
    </div>
  );
}
